import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Btn, Overline } from "@/components/kit";
import { Maximize2, X, Download } from "lucide-react";
import architecture from "@/assets/architecture.webp";
import modes from "@/assets/modes.webp";

const PDF_URL = `${process.env.PUBLIC_URL || ""}/worked-example.pdf`;

const TABS = [
  { key: "architecture", label: "Architecture", kind: "image", img: architecture,
    title: "The Architecture of Human Experience", subtitle: "A map for navigation — the 12 + 1 dimensions",
    alt: "The Architecture of Human Experience: the 12 + 1 dimensions of experience arranged around awareness." },
  { key: "modes", label: "12 Modes", kind: "image", img: modes,
    title: "The 12 Modes of Human Experience", subtitle: "How we meet the world — coherent & protective expressions",
    alt: "The 12 Modes of Human Experience, each with its coherent and protective expression." },
  { key: "worked", label: "Worked Example", kind: "pdf",
    title: "Worked Example", subtitle: "A completed worked example to keep beside you." },
];

export default function Reference() {
  const location = useLocation();
  const q = new URLSearchParams(location.search).get("chart");
  const initial = TABS.some((t) => t.key === q) ? q : "architecture";
  const [tab, setTab] = useState(initial);
  const [zoom, setZoom] = useState(false);
  const active = TABS.find((t) => t.key === tab);

  return (
    <div className="max-w-3xl mx-auto em-fade-up" data-testid="reference-page">
      <Overline>The Map</Overline>
      <h1 className="font-serif text-4xl sm:text-5xl text-ink mt-2 mb-6">Reference</h1>

      <div className="inline-flex flex-wrap bg-panel border border-line rounded-full p-1 mb-6">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => { setTab(t.key); setZoom(false); }} data-testid={`ref-tab-${t.key}`}
            className={`px-5 py-2 rounded-full text-sm transition-colors ${
              tab === t.key ? "bg-golddark text-gold" : "text-inkfaint hover:text-ink"
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {active.kind === "image" ? (
        <>
          <div className="relative bg-panel border border-line rounded-2xl p-3">
            <button onClick={() => setZoom(true)} data-testid="ref-expand"
              className="absolute top-5 right-5 z-10 p-2 rounded-full bg-surface/70 text-ink hover:bg-surface">
              <Maximize2 className="w-4 h-4" />
            </button>
            <img src={active.img} alt={active.alt} onClick={() => setZoom(true)}
              className="w-full rounded-xl cursor-zoom-in bg-white" data-testid="ref-image" />
          </div>
          <p className="text-inkfaint text-sm mt-4">
            <span className="text-ink">{active.title}</span> — {active.subtitle}. <span className="text-faint">tap expand to zoom</span>
          </p>
        </>
      ) : (
        <>
          <div className="bg-panel border border-line rounded-2xl p-3">
            <iframe src={PDF_URL} title="Worked Example" data-testid="ref-pdf"
              className="w-full h-[72vh] rounded-xl bg-white" />
          </div>
          <div className="flex items-center justify-between mt-4 gap-4 flex-wrap">
            <p className="text-inkfaint text-sm">
              <span className="text-ink">{active.title}</span> — {active.subtitle}
            </p>
            <Btn as="a" href={PDF_URL} download="Worked Example.pdf" target="_blank" rel="noopener noreferrer"
              variant="outline" className="h-11 px-5" data-testid="ref-pdf-download">
              <Download className="w-4 h-4" /> Download PDF
            </Btn>
          </div>
        </>
      )}

      {zoom && active.kind === "image" && (
        <div className="fixed inset-0 z-50 bg-black/90 overflow-auto" onClick={() => setZoom(false)} data-testid="ref-zoom">
          <button className="fixed top-4 right-4 z-50 p-2 rounded-full bg-surface/80 text-ink" onClick={() => setZoom(false)} data-testid="ref-zoom-close">
            <X className="w-5 h-5" />
          </button>
          <div className="min-h-full flex items-start justify-center p-4">
            <img src={active.img} alt={active.alt} className="max-w-none w-[min(1400px,180%)] rounded-lg bg-white" onClick={(e) => e.stopPropagation()} />
          </div>
        </div>
      )}
    </div>
  );
}
